import { useState, useEffect } from 'react'
import { recruiterApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card'
import { Textarea } from '../components/ui/Textarea'
import { Badge } from '../components/ui/Badge'
import { RotateCcw, Loader2, Trash2, Check, X, ChevronDown } from 'lucide-react'
import type { RecruiterState, Question } from '../types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

type QuestionType = 'MCQ' | 'Open'

const defaultQuestionTypes = (): QuestionType[] =>
  Array.from({ length: 10 }, (_, index) => (index < 5 ? 'Open' : 'MCQ'))

function FormCreationPage() {
  const { recruiter } = useAuth()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [state, setState] = useState<RecruiterState>({})
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    jd: '',
    difficulty: 3,
    questionTypes: defaultQuestionTypes(),
  })
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('jd')
  const [resetting, setResetting] = useState(false)
  const [hasSavedCurrentDraft, setHasSavedCurrentDraft] = useState(false)
  const [composeOpen, setComposeOpen] = useState(true)

  const hasUnsavedTest = !!state.approved_questions?.length && !hasSavedCurrentDraft

  // Initialize session on mount
  useEffect(() => {
    let cancelled = false

    const loadState = async () => {
      const storedSessionId = localStorage.getItem('form_id')
      if (storedSessionId) {
        setSessionId(storedSessionId)
        try {
          const response = await recruiterApi.getState(storedSessionId)
          if (cancelled) return
          setState(response.state || {})
          if (response.state) {
            setFormData(prev => ({
              ...prev,
              jd: prev.jd.trim() ? prev.jd : (response.state.jd || ''),
              difficulty: response.state.difficulty ?? prev.difficulty,
            }))
            if (response.state.proposed_questions?.length) {
              setComposeOpen(false)
            }
          }
        } catch (err: any) {
          console.error('Failed to load state:', err)
        }
      } else {
        // Create new session on first load
        try {
          const response = await recruiterApi.getState()
          if (cancelled) return
          const newSessionId = response.form_id
          setSessionId(newSessionId)
          localStorage.setItem('form_id', newSessionId)
        } catch (err: any) {
          console.error('Failed to create session:', err)
        }
      }
    }
    loadState()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!hasUnsavedTest) return

    const confirmLeave = () => {
      return window.confirm('יש שינויים שלא נשמרו. לצאת בכל זאת?')
    }

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const anchor = target?.closest('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) return

      const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      if (isModifiedClick) return

      const destination = new URL(anchor.href, window.location.href)
      const isSameOrigin = destination.origin === window.location.origin
      const isDifferentPath = destination.pathname !== window.location.pathname || destination.search !== window.location.search
      if (!isSameOrigin || !isDifferentPath) return

      if (confirmLeave()) return

      event.preventDefault()
      event.stopPropagation()
    }

    const handlePopState = () => {
      const shouldLeave = window.confirm('יש שינויים שלא נשמרו. לצאת בכל זאת?')
      if (shouldLeave) return

      window.history.pushState(null, '', window.location.href)
    }

    window.history.pushState(null, '', window.location.href)
    document.addEventListener('click', handleDocumentClick, true)
    window.addEventListener('popstate', handlePopState)

    return () => {
      document.removeEventListener('click', handleDocumentClick, true)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [hasUnsavedTest])

  useEffect(() => {
    if (!hasUnsavedTest) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedTest])

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!sessionId) {
      setError('חסר מזהה סשן. אנא רענן את הדף.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await recruiterApi.start(
        formData.jd,
        formData.difficulty,
        formData.questionTypes,
        sessionId,
      )
      setHasSavedCurrentDraft(false)
      // Reload state after starting
      const response = await recruiterApi.getState(sessionId)
      setState(response.state || {})
      if (response.state?.proposed_questions?.length) {
        setComposeOpen(false)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה ביצירת השאלות')
    } finally {
      setLoading(false)
    }
  }

  const handleDecision = async (decision: 'approve' | 'reject', questionIndex: number) => {
    if (!sessionId) {
      setError('חסר מזהה סשן. אנא התחל סשן חדש.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await recruiterApi.decide(decision, sessionId, questionIndex)
      setHasSavedCurrentDraft(false)
      // Reload state after decision
      const response = await recruiterApi.getState(sessionId)
      setState(response.state || {})
      if (!response.state?.proposed_questions?.length) {
        setComposeOpen(true)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה בקבלת החלטה')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!sessionId) {
      setError('חסר מזהה סשן. אנא התחל סשן חדש.')
      return
    }

    if (!recruiter) {
      setError('נדרש להתחבר כדי לשמור. אנא התחבר מחדש.')
      return
    }

    if (!state.approved_questions || state.approved_questions.length === 0) {
      setError('אין שאלות מאושרות לשמירה. אנא אשר שאלות לפני השמירה.')
      return
    }

    setLoading(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const result = await recruiterApi.save(sessionId)
      setHasSavedCurrentDraft(true)
      setSuccessMessage(result.message || 'השאלות נשמרו בהצלחה')
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      console.error('Error saving form:', err)
      const errorMessage = err.response?.data?.message || err.message || 'שגיאה בשמירה'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async () => {
    setResetting(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Clear localStorage session ID
      localStorage.removeItem('form_id')
      
      // Create new session
      const response = await recruiterApi.getState()
      const newFormId = response.form_id
      
      // Update state
      setSessionId(newFormId)
      setState({})
      setFormData({ jd: '', difficulty: 3, questionTypes: defaultQuestionTypes() })
      setComposeOpen(true)
      setHasSavedCurrentDraft(false)
      localStorage.setItem('form_id', newFormId)
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה באיפוס הטופס')
      // Even if API call fails, clear local state
      setState({})
      setFormData({ jd: '', difficulty: 3, questionTypes: defaultQuestionTypes() })
      setComposeOpen(true)
      setHasSavedCurrentDraft(false)
      localStorage.removeItem('form_id')
    } finally {
      setResetting(false)
    }
  }

  const handleRemoveQuestion = async (questionIndex: number) => {
    if (!sessionId) {
      setError('חסר מזהה סשן. אנא התחל סשן חדש.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await recruiterApi.removeQuestion(sessionId, questionIndex)
      setHasSavedCurrentDraft(false)
      // Reload state after removal
      const response = await recruiterApi.getState(sessionId)
      setState(response.state || {})
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה בהסרת שאלה')
    } finally {
      setLoading(false)
    }
  }

  const openCount = formData.questionTypes.filter((type) => type === 'Open').length
  const mcqCount = formData.questionTypes.length - openCount

  const setQuestionType = (index: number, qType: QuestionType) => {
    setFormData((prev) => ({
      ...prev,
      questionTypes: prev.questionTypes.map((type, typeIndex) => (
        typeIndex === index ? qType : type
      )),
    }))
  }

  const allOpen = openCount === formData.questionTypes.length
  const toggleAllQuestionTypes = () => {
    const nextType: QuestionType = allOpen ? 'MCQ' : 'Open'
    setFormData((prev) => ({
      ...prev,
      questionTypes: prev.questionTypes.map(() => nextType),
    }))
  }

  return (
    <div className="bg-background py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              יצירת שאלון
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* {recruiter && (
              <span className="text-sm text-gray-600">שלום, {recruiter.username}</span>
            )} */}
            <Button 
              variant="outline" 
              onClick={handleReset}
              disabled={resetting}
              className="text-sm flex items-center gap-2"
            >
              {resetting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  מאפס...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  איפוס
                </>
              )}
            </Button>
            {/* <Button 
              variant="outline" 
              onClick={handleLogout}
              className="text-sm"
            >
              התנתק
            </Button> */}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
            {successMessage}
          </div>
        )}



        <Tabs defaultValue="jd" className="w-full rtl" dir="rtl" value={activeTab}>
          <TabsList>
            <TabsTrigger value="jd" onClick={()=>{setActiveTab('jd')}}>משרה</TabsTrigger>
            <TabsTrigger value="create_question" onClick={()=>{setActiveTab('create_question')}}>יצירת שאלה</TabsTrigger>
            <TabsTrigger value="approved_questions" onClick={()=>{setActiveTab('approved_questions')}}>סיכום</TabsTrigger>
          </TabsList>



          <TabsContent value="jd">
            {/* Start Form */}
            <Card className="mb-6 border border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">יצירת שאלת ריאיון</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleStart} className="space-y-4">
                  <Textarea
                    label="תיאור משרה"
                    name="jd"
                    rows={4}
                    placeholder="הדבק תיאור משרה כאן..."
                    value={formData.jd}
                    onChange={(e) => {
                      const jd = e.target.value
                      setFormData(prev => ({ ...prev, jd }))
                      setState(prev => {
                        if ((prev.jd || '').trim() === jd.trim()) return prev
                        return { ...prev, pending_question: undefined, proposed_questions: undefined }
                      })
                      if ((state.jd || '').trim() !== jd.trim()) {
                        setComposeOpen(true)
                      }
                    }}
                    required
                  />
                  <Button className='w-full' onClick={()=>{setActiveTab('create_question')}} type='button'>עבור ליצירת שאלה</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>






          <TabsContent value="create_question">
              <div>
              <Collapsible open={composeOpen} onOpenChange={setComposeOpen}>
              <Card className="mb-6 border border-gray-200 shadow-sm">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer">
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-lg font-semibold">יצירת שאלות ריאיון</CardTitle>
                      <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${composeOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                <CardContent>
                    <form onSubmit={handleStart} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">רמת קושי</label>
                        <div className="grid grid-cols-5 gap-2">
                          {[1, 2, 3, 4, 5].map((level) => (
                            <Button
                              key={level}
                              type="button"
                              variant={formData.difficulty === level ? 'default' : 'outline'}
                              onClick={() => setFormData((prev) => ({ ...prev, difficulty: level }))}
                            >
                              {level}
                            </Button>
                          ))}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">1 הקלה ביותר, 5 הקשה ביותר</p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <label className="block text-sm font-medium text-gray-700">סוג כל שאלה</label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={toggleAllQuestionTypes}
                          >
                            {allOpen ? 'הכל אמריקאי' : 'הכל פתוחות'}
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {formData.questionTypes.map((qType, index) => (
                            <div key={index} className="flex items-center justify-between gap-3">
                              <span className="text-sm text-gray-700 w-16">שאלה {index + 1}</span>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={qType === 'Open' ? 'default' : 'outline'}
                                  onClick={() => setQuestionType(index, 'Open')}
                                >
                                  פתוחה
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={qType === 'MCQ' ? 'default' : 'outline'}
                                  onClick={() => setQuestionType(index, 'MCQ')}
                                >
                                  אמריקאי
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">פתוחות: {openCount} · אמריקאי: {mcqCount}</p>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading || !formData.jd.trim()}
                        className="w-full"
                      >
                        {loading ? 'יוצר 10 שאלות...' : 'צור 10 שאלות'}
                      </Button>
                    </form>
                </CardContent>
                </CollapsibleContent>
                </Card>
              </Collapsible>

            {state.proposed_questions && state.proposed_questions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">
                    שאלות מוצעות ({state.proposed_questions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {state.proposed_questions.map((question, index) => (
                      <li key={index} className="p-4 bg-gray-50 rounded-md border border-gray-200 flex justify-between items-start gap-3">
                        <div className="flex-1 space-y-2">
                          <h3 className="text-sm font-medium text-gray-900">{question.text}</h3>
                          <Badge variant="default">
                            סוג: {question.q_type === 'MCQ' ? 'אמריקאי' : 'פתוחה'}
                          </Badge>
                          {question.options && question.options.length > 0 && (
                            <ul className="list-disc list-inside space-y-1 mr-4 text-sm text-gray-600">
                              {question.options.map((opt, optIndex) => (
                                <li key={optIndex}>{opt}</li>
                              ))}
                            </ul>
                          )}
                          {question.answer && (
                            <p className="text-sm text-gray-600">תשובה: {question.answer}</p>
                          )}
                          {question.rationale && (
                            <p className="text-xs text-gray-600">{question.rationale}</p>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="אשר שאלה"
                            onClick={() => handleDecision('approve', index)}
                            disabled={loading}
                            className="h-7 w-7 text-slate-600 hover:text-slate-900"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="דחה שאלה"
                            onClick={() => handleDecision('reject', index)}
                            disabled={loading}
                            className="h-7 w-7 text-slate-600 hover:text-slate-900"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
              </div>
          </TabsContent>










          <TabsContent value="approved_questions">
            {/* Approved Questions */}
            { state.approved_questions && state.approved_questions.length > 0 && (
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-semibold">
                      שאלות שאושרו ({state.approved_questions.length})
                    </CardTitle>
                    <Button 
                      variant="outline" 
                      onClick={handleSave} 
                      disabled={loading}
                      className="text-sm"
                    >
                      {loading ? 'שומר...' : 'שמור'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {state.approved_questions.map((q: Question, idx: number) => (
                      <li key={idx} className="p-4 bg-gray-50 rounded-md border border-gray-200 flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <h4 className="font-medium mb-1 text-gray-900 text-sm">{q.text}</h4>
                          {q.rationale && (
                            <p className="text-xs text-gray-600 mt-1">{q.rationale}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveQuestion(idx)}
                          disabled={loading}
                          className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          </Tabs>
        </div>
      </div>
  )
}

export default FormCreationPage
